// Single source of truth for how a route's track is styled - shared by
// MapView.vue, the admin "Стиль маршрутов" editor (RouteStyleSection.vue),
// RouteMiniMap.vue and ImportPointsTool.vue so none of them can drift from
// what a route line is supposed to look like. The config shape mirrors
// GET /route-style/active exactly (see backend/app/route_styles/schemas.py) -
// keep both in sync when adding a knob.
//
// Engine-agnostic on purpose: this module only computes geometry/color, no
// Leaflet or MapLibre types anywhere in it (the app is MapLibre-only now,
// see routeLineStyleMaplibre.ts for the actual drawing side). Distance/
// bearing math is a plain spherical-earth haversine (haversineDistanceM),
// not a mapping-library method - the same approach destinationPoint below
// already used for the inverse problem.

export interface RouteStyleMain {
  color: string;
  weight: number;
  opacity: number;
  dashArray: string | null;
  lineCap: "round" | "butt" | "square";
  lineJoin: "round" | "bevel" | "miter";
  smoothFactor: number;
  // "arrows" replaces the continuous stroke with a tight chain of chevrons
  // tracing the same path - color/opacity/weight/casing/glow all still
  // apply exactly as they do for "line" (see renderArrowLine below), this
  // only changes the shape of the main line itself.
  style: "line" | "arrows";
  arrowSpacingM: number;
  arrowWingLengthM: number;
  arrowWingAngleDeg: number;
}

export interface RouteStyleGradient {
  maxGradePercent: number;
  hueStart: number;
  hueEnd: number;
  saturation: number;
  lightness: number;
  neutralColor: string;
  segmentWindowM: number;
}

export interface RouteStyleCasing {
  enabled: boolean;
  color: string;
  extraWidth: number;
  opacity: number;
  dashArray: string | null;
}

export interface RouteStyleGlow {
  enabled: boolean;
  color: string;
  blur: number;
  opacity: number;
}

export interface RouteStyleArrows {
  enabled: boolean;
  color: string;
  strokeColor: string;
  strokeWidth: number;
  size: number;
  spacingM: number;
  opacity: number;
  shape: "triangle" | "chevron";
}

export interface RouteStyleConfig {
  mode: "single" | "gradient";
  main: RouteStyleMain;
  gradient: RouteStyleGradient;
  casing: RouteStyleCasing;
  glow: RouteStyleGlow;
  arrows: RouteStyleArrows;
}

// Matches the seed row from the 20260728_01 migration / service.py's
// FALLBACK_CONFIG - used before the real config has loaded and if the fetch
// ever fails, so the map never renders an unstyled line.
export const DEFAULT_ROUTE_STYLE: RouteStyleConfig = {
  mode: "gradient",
  main: { color: "#1f7a5c", weight: 5, opacity: 1, dashArray: null, lineCap: "round", lineJoin: "round", smoothFactor: 1, style: "line", arrowSpacingM: 25, arrowWingLengthM: 10, arrowWingAngleDeg: 35 },
  gradient: { maxGradePercent: 30, hueStart: 120, hueEnd: 0, saturation: 75, lightness: 42, neutralColor: "#6b7280", segmentWindowM: 90 },
  casing: { enabled: true, color: "#ffffff", extraWidth: 2, opacity: 1, dashArray: null },
  glow: { enabled: false, color: "#000000", blur: 1, opacity: 0.65 },
  arrows: { enabled: false, color: "#ffffff", strokeColor: "#1f2937", strokeWidth: 1.5, size: 14, spacingM: 150, opacity: 1, shape: "triangle" },
};

// Merges a (possibly partial / older-schema) config from the API over the
// defaults, so a missing section (e.g. `casing`) can never crash the renderer.
export function normalizeRouteStyle(raw: Partial<RouteStyleConfig> | null | undefined): RouteStyleConfig {
  const merged = { ...DEFAULT_ROUTE_STYLE } as Record<string, unknown>;
  for (const key of Object.keys(DEFAULT_ROUTE_STYLE) as (keyof RouteStyleConfig)[]) {
    const def = DEFAULT_ROUTE_STYLE[key];
    const val = raw?.[key];
    merged[key] = def && typeof def === "object" && val && typeof val === "object" ? { ...def, ...val } : (val ?? def);
  }
  return merged as unknown as RouteStyleConfig;
}

export type PointLLE = [lat: number, lng: number, elevationM: number | null];

export function slopeToColor(gradePercent: number, g: RouteStyleGradient): string {
  const t = Math.min(Math.abs(gradePercent), g.maxGradePercent) / g.maxGradePercent;
  const hue = g.hueStart + (g.hueEnd - g.hueStart) * t;
  return `hsl(${hue.toFixed(0)}, ${g.saturation}%, ${g.lightness}%)`;
}

export interface GradientSegment {
  // Plain [lat, lng] tuples - was L.LatLng[] before the Leaflet removal;
  // routeLineStyleMaplibre.ts's buildRouteLineFeatures is the only consumer
  // and just needs the coordinates, not a mapping-library point type.
  latlngs: [number, number][];
  color: string;
  // Cumulative distance (m) from the route start - lets
  // routeLineStyleMaplibre.ts's buildRouteLineFeatures look up which
  // segment's color an arrow chevron (also distance-stamped, see
  // ArrowPlacement) falls into without re-walking the whole track.
  startDistanceM: number;
  endDistanceM: number;
}

// Spherical-earth great-circle distance in meters - same radius/approach as
// destinationPoint below (its inverse problem). Leaflet's LatLng.distanceTo
// used the same haversine formula under the hood, so this is a like-for-like
// replacement, not an approximation swap.
export function haversineDistanceM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const phi1 = lat1 * toRad;
  const phi2 = lat2 * toRad;
  const dPhi = (lat2 - lat1) * toRad;
  const dLambda = (lng2 - lng1) * toRad;
  const a = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function buildGradientSegments(points: PointLLE[], g: RouteStyleGradient): GradientSegment[] {
  const segments: GradientSegment[] = [];
  if (points.length < 2) return segments;

  let windowStart = 0;
  let windowDistanceM = 0;
  let totalDistanceM = 0;

  for (let i = 1; i < points.length; i++) {
    const segLen = haversineDistanceM(points[i - 1][0], points[i - 1][1], points[i][0], points[i][1]);
    windowDistanceM += segLen;
    totalDistanceM += segLen;

    const isLastPoint = i === points.length - 1;
    if (windowDistanceM < g.segmentWindowM && !isLastPoint) continue;

    const elevStart = points[windowStart][2];
    const elevEnd = points[i][2];
    let color = g.neutralColor;
    if (elevStart != null && elevEnd != null && windowDistanceM > 0) {
      color = slopeToColor(((elevEnd - elevStart) / windowDistanceM) * 100, g);
    }
    segments.push({
      latlngs: points.slice(windowStart, i + 1).map(([lat, lng]) => [lat, lng] as [number, number]),
      color,
      startDistanceM: totalDistanceM - windowDistanceM,
      endDistanceM: totalDistanceM,
    });
    windowStart = i;
    windowDistanceM = 0;
  }
  return segments;
}

export function bearingDeg(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = Math.PI / 180;
  const toDeg = 180 / Math.PI;
  const phi1 = lat1 * toRad;
  const phi2 = lat2 * toRad;
  const dLambda = (lng2 - lng1) * toRad;
  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  return ((Math.atan2(y, x) * toDeg) + 360) % 360;
}

// Inverse of bearingDeg+distanceTo: given a start point, a bearing and a
// ground distance, returns the destination point. Used to place each
// chevron's two wings relative to its tip (renderArrowLine) - working in
// real ground meters (not screen pixels) keeps wing length proportional to
// the route's own geometry at every zoom, same as arrowSpacingM already is.
export function destinationPoint(lat: number, lng: number, bearingDegVal: number, distanceM: number): [number, number] {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const toDeg = 180 / Math.PI;
  const brng = bearingDegVal * toRad;
  const phi1 = lat * toRad;
  const lambda1 = lng * toRad;
  const delta = distanceM / R;
  const phi2 = Math.asin(
    Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(brng)
  );
  const lambda2 =
    lambda1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(delta) * Math.cos(phi1),
      Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2)
    );
  return [phi2 * toDeg, ((lambda2 * toDeg + 540) % 360) - 180];
}

export interface ArrowPlacement {
  lat: number;
  lng: number;
  bearing: number;
  distanceM: number;
}

// Walks the track accumulating real ground distance (not screen pixels, so
// spacing stays correct in meters at every zoom level) and drops an arrow
// every spacingM, offset by half a spacing so arrows aren't bunched at the
// very start of the track.
export function computeArrowPlacements(points: PointLLE[], spacingM: number): ArrowPlacement[] {
  const placements: ArrowPlacement[] = [];
  if (spacingM <= 0 || points.length < 2) return placements;

  let dist = 0;
  let nextMark = spacingM / 2;

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const segLen = haversineDistanceM(a[0], a[1], b[0], b[1]);
    if (segLen === 0) continue;
    const bearing = bearingDeg(a[0], a[1], b[0], b[1]);
    while (dist + segLen >= nextMark) {
      const t = (nextMark - dist) / segLen;
      placements.push({
        lat: a[0] + (b[0] - a[0]) * t,
        lng: a[1] + (b[1] - a[1]) * t,
        bearing,
        distanceM: nextMark,
      });
      nextMark += spacingM;
    }
    dist += segLen;
  }
  return placements;
}

export function colorForDistance(segments: GradientSegment[], distanceM: number, fallback: string): string {
  for (const seg of segments) {
    if (distanceM >= seg.startDistanceM && distanceM <= seg.endDistanceM) return seg.color;
  }
  return segments.length ? segments[segments.length - 1].color : fallback;
}

// Drawing itself lives in routeLineStyleMaplibre.ts (buildRouteLineFeatures/
// buildRouteLinePaint/buildArrowIconImage) - this used to also export a
// Leaflet renderRouteLine(group, points, cfg) that drew straight into an
// L.LayerGroup (plus its own renderArrowLine/arrowDivIcon helpers), removed
// once RouteMiniMap.vue/RouteStyleSection.vue/ImportPointsTool.vue migrated
// off Leaflet - every consumer now goes through the MapLibre path above.
